import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/router";
import { useHr } from "@/hooks/useHr";
import styles from "./HRNotifications.module.css";
import { formatDate as formatDateUtil } from "@/utils/formatDate";

const HRNotifications = ({ userId }) => {
  const router = useRouter();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const notificationRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const {
    notifications,
    totalCount,
    fetchEnquiryNotifications,
    fetchSelectionNotifications,
    fetchJobApplicationNotifications,
    fetchEventEnrollmentNotifications,
    markEnquiriesAsViewed,
    markNotificationAsRead,
  } = useHr(userId, "hr");

  const jobCount = notifications.jobApplications.count + notifications.selections.count;
  const eventsCount = notifications.eventEnrollments.count;
  const enquiriesCount = notifications.enquiries.count;

  const tabs = [
    { id: "all", label: "All", count: totalCount },
    { id: "job", label: "Job", count: jobCount },
    { id: "events", label: "Events", count: eventsCount },
    { id: "enquiries", label: "Enquiries", count: enquiriesCount },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setOverlayOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchAll = () => {
      fetchEnquiryNotifications();
      fetchSelectionNotifications();
      fetchJobApplicationNotifications();
      fetchEventEnrollmentNotifications();
    };

    const intervalId = setInterval(fetchAll, 30000);
    return () => clearInterval(intervalId);
  }, [
    userId,
    fetchEnquiryNotifications,
    fetchSelectionNotifications,
    fetchJobApplicationNotifications,
    fetchEventEnrollmentNotifications,
  ]);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

    if (totalCount > 0) {
      setOverlayOpen(true);
      Object.entries(notifications).forEach(([key, val]) => {
        if (val.count > 0 || val.data.length === 0) {
          const fetchMap = {
            enquiries: fetchEnquiryNotifications,
            selections: fetchSelectionNotifications,
            jobApplications: fetchJobApplicationNotifications,
            eventEnrollments: fetchEventEnrollmentNotifications,
          };
          fetchMap[key]?.();
        }
      });
    }
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setOverlayOpen(false), 200);
  };

  const formatDate = (dateString) => {
    const diff = Date.now() - new Date(dateString);
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDateUtil(dateString);
  };

  const getNotificationTime = (notification, type) => {
    if (type === "jobApplications") {
      return notification?.updatedAt || notification?.createdAt;
    }
    return notification?.createdAt;
  };

  const getNotificationContext = (notification, type) => {
    if (type === "eventEnrollments") {
      return {
        badge: "EVENT",
        label: "Event",
        itemName:
          notification?.metadata?.itemName ||
          notification?.metadata?.eventName ||
          notification?.metadata?.title ||
          "Event registration",
      };
    }

    if (type === "jobApplications") {
      return {
        badge: "JOB",
        label: "Job",
        itemName:
          notification?.metadata?.jobTitle ||
          notification?.metadata?.itemName ||
          "Job application",
      };
    }

    return {
      badge: "INFO",
      label: "Notification",
      itemName: notification?.title || "Update",
    };
  };

  const notificationHandlers = {
    enquiries: async () => {
      await markEnquiriesAsViewed();
      await fetchEnquiryNotifications();
      setOverlayOpen(false);
      router.push("/hr-admin/hr-enquiries");
    },
    selections: async (id) => {
      await markNotificationAsRead(id, "selections");
      await fetchSelectionNotifications();
      setOverlayOpen(false);
      router.push("/hr-admin/hr-enquiries?tab=selected");
    },
    jobApplications: async (notification) => {
      await markNotificationAsRead(notification._id, "jobApplications");
      await fetchJobApplicationNotifications();
      setOverlayOpen(false);
      router.push(`/hr-admin/job-applicants/${notification.referenceId}`);
    },
    eventEnrollments: async (notification) => {
      await markNotificationAsRead(notification._id, "eventEnrollments");
      await fetchEventEnrollmentNotifications();
      setOverlayOpen(false);
      router.push(`/event-details/${notification.referenceId}`);
    },
  };

  const NotificationSection = ({ title, notifications: notifs, loading, renderItem }) => {
    const unread = notifs.filter((n) => !n.isRead);
    if (unread.length === 0 && !loading) return null;

    return (
      <>
        <div className="rbt-notification-section">
          <div className="rbt-job-overlay-header">
            <h6 className="rbt-job-overlay-title">{title}</h6>
          </div>

          {loading ? (
            <div className="rbt-job-overlay-loading">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Loading...</span>
            </div>
          ) : (
            <ul className="rbt-job-overlay-list">{unread.map(renderItem)}</ul>
          )}
        </div>
        <div className="rbt-notification-divider"></div>
      </>
    );
  };

  const showEvents = activeTab === "all" || activeTab === "events";
  const showJobs = activeTab === "all" || activeTab === "job";
  const showEnquiries = activeTab === "all" || activeTab === "enquiries";

  const activeTabCountMap = {
    all: totalCount,
    job: jobCount,
    events: eventsCount,
    enquiries: enquiriesCount,
  };

  const activeTabLoadingMap = {
    all:
      notifications.eventEnrollments.loading ||
      notifications.jobApplications.loading ||
      notifications.selections.loading ||
      notifications.enquiries.loading,
    job: notifications.jobApplications.loading || notifications.selections.loading,
    events: notifications.eventEnrollments.loading,
    enquiries: notifications.enquiries.loading,
  };

  return (
    <>
      <style jsx>{`
        .rbt-notification-section { padding: 0; }
        .rbt-notification-divider { height: 1px; background: #e0e0e0; margin: 10px 0; }
        .rbt-selection-notification { transition: background-color 0.2s, border-color 0.2s; border-left: 4px solid #94a3b8; border-radius: 6px; margin-bottom: 8px; padding-left: 10px; }
        .rbt-selection-notification.unread { background-color: #f8fafc; }
        .rbt-selection-notification:hover { background-color: #f1f5f9; }
        .rbt-selection-notification.event-card { border-left-color: #0f766e; background-color: #f0fdfa; }
        .rbt-selection-notification.event-card:hover { background-color: #ccfbf1; }
        .rbt-selection-notification.job-card { border-left-color: #1d4ed8; background-color: #eff6ff; }
        .rbt-selection-notification.job-card:hover { background-color: #dbeafe; }
        .rbt-selection-notification-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .rbt-job-notification-overlay { max-height: 500px; overflow-y: auto; }
        .rbt-job-overlay-content { display: flex; flex-direction: column; }
        .rbt-item-topline { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; gap: 8px; }
        .rbt-item-badge { font-size: 10px; font-weight: 800; letter-spacing: .5px; border-radius: 999px; padding: 3px 10px; color: #0f172a; background: #e2e8f0; }
        .rbt-selection-notification.event-card .rbt-item-badge { background: #99f6e4; color: #134e4a; }
        .rbt-selection-notification.job-card .rbt-item-badge { background: #bfdbfe; color: #1e3a8a; }
        .rbt-item-context { font-size: 12px; color: #334155; margin-bottom: 4px; }
        .rbt-item-context strong { color: #0f172a; font-weight: 700; }
        .rbt-item-message { font-size: 13px; color: #475569; line-height: 1.35; margin-bottom: 4px; }
        .rbt-item-time { font-size: 12px; color: #64748b; white-space: nowrap; font-weight: 600; }
        .rbt-job-overlay-title-text strong { font-size: 15px; line-height: 1.3; display: inline-block; }

        .rbt-notification-tabs {
          display: flex;
          gap: 8px;
          padding: 10px;
          border-bottom: 1px solid #e2e8f0;
          position: sticky;
          top: 0;
          background: #fff;
          z-index: 2;
        }

        .rbt-notification-tab {
          border: 1px solid #cbd5e1;
          background: #f8fafc;
          color: #334155;
          border-radius: 999px;
          padding: 5px 10px;
          font-size: 12px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }

        .rbt-notification-tab.active {
          background: #e0f2fe;
          border-color: #7dd3fc;
          color: #0c4a6e;
        }

        .rbt-notification-tab-count {
          background: #fff;
          border-radius: 999px;
          min-width: 20px;
          padding: 0 6px;
          text-align: center;
          font-weight: 700;
        }
      `}</style>

      <li className="access-icon rbt-notification-wrapper rbt-notification-position" ref={notificationRef}>
        <Link
          className="rbt-round-btn rbt-notification-btn"
          href="/notifications"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            router.push("/notifications");
          }}
          title="Notifications"
        >
          <i className="feather-bell"></i>
          {totalCount > 0 && (
            <span className="rbt-notification-count">{totalCount > 99 ? "99+" : totalCount}</span>
          )}
        </Link>

        {overlayOpen && (
          <div
            className="rbt-job-notification-overlay"
            onMouseEnter={() => hoverTimeoutRef.current && clearTimeout(hoverTimeoutRef.current)}
            onMouseLeave={() => setOverlayOpen(false)}
          >
            <div className="rbt-notification-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`rbt-notification-tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span>{tab.label}</span>
                  <span className="rbt-notification-tab-count">{tab.count}</span>
                </button>
              ))}
            </div>

            {showEvents && (
              <NotificationSection
                title="Event Enrollments"
                notifications={notifications.eventEnrollments.data}
                loading={notifications.eventEnrollments.loading}
                renderItem={(n) => (
                  <li
                    key={n._id}
                    className="rbt-job-overlay-item rbt-selection-notification event-card unread"
                    onClick={() => notificationHandlers.eventEnrollments(n)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="rbt-job-overlay-content">
                      <div className="rbt-item-topline">
                        <span className="rbt-item-badge">{getNotificationContext(n, "eventEnrollments").badge}</span>
                        <span className="rbt-item-time">{formatDate(getNotificationTime(n, "eventEnrollments"))}</span>
                      </div>
                      <div className="rbt-selection-notification-header">
                        <div className="rbt-job-overlay-title-text">
                          <strong>{n.title}</strong>
                        </div>
                      </div>
                      <div className="rbt-item-context">
                        {getNotificationContext(n, "eventEnrollments").label}: <strong>{getNotificationContext(n, "eventEnrollments").itemName}</strong>
                      </div>
                      <div className="rbt-item-message">{n.message}</div>
                      <Link
                        href={`/event-details/${n.referenceId}`}
                        className="rbt-job-overlay-view-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          notificationHandlers.eventEnrollments(n);
                        }}
                      >
                        View Event Details →
                      </Link>
                    </div>
                  </li>
                )}
              />
            )}

            {showJobs && (
              <NotificationSection
                title="Job Applications"
                notifications={notifications.jobApplications.data}
                loading={notifications.jobApplications.loading}
                renderItem={(n) => (
                  <li
                    key={n._id}
                    className="rbt-job-overlay-item rbt-selection-notification job-card unread"
                    onClick={() => notificationHandlers.jobApplications(n)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="rbt-job-overlay-content">
                      <div className="rbt-item-topline">
                        <span className="rbt-item-badge">{getNotificationContext(n, "jobApplications").badge}</span>
                        <span className="rbt-item-time">{formatDate(getNotificationTime(n, "jobApplications"))}</span>
                      </div>
                      <div className="rbt-selection-notification-header">
                        <div className="rbt-job-overlay-title-text">
                          <strong>{n.title}</strong>
                        </div>
                      </div>
                      <div className="rbt-item-context">
                        {getNotificationContext(n, "jobApplications").label}: <strong>{getNotificationContext(n, "jobApplications").itemName}</strong>
                      </div>
                      <div className="rbt-item-message">{n.message}</div>
                      <Link
                        href={`/hr-admin/job-applicants/${n.referenceId}`}
                        className="rbt-job-overlay-view-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          notificationHandlers.jobApplications(n);
                        }}
                      >
                        View Applications →
                      </Link>
                    </div>
                  </li>
                )}
              />
            )}

            {showEnquiries && notifications.enquiries.count > 0 && (
              <div className="rbt-notification-section">
                <div className="rbt-job-overlay-header">
                  <h6 className="rbt-job-overlay-title">Recent Enquiries</h6>
                </div>
                {notifications.enquiries.loading ? (
                  <div className="rbt-job-overlay-loading">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span className="ms-2">Loading...</span>
                  </div>
                ) : (
                  <>
                    <ul className="rbt-job-overlay-list">
                      {notifications.enquiries.data.map((n, i) => (
                        <li key={n._id || i} className="rbt-job-overlay-item">
                          <div className="rbt-job-overlay-content">
                            <div className="rbt-job-overlay-title-text">
                              <strong>{n.studentName}</strong>
                              <span className="rbt-job-overlay-time" style={{ marginLeft: "8px" }}>
                                {formatDate(n.createdAt)}
                              </span>
                            </div>
                            <div className="rbt-job-overlay-company">{n.categoryName}</div>
                            <div className="rbt-job-overlay-company">
                              {n.message?.length > 20 ? `${n.message.substring(0, 20)}...` : n.message}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="rbt-job-overlay-footer">
                      <Link
                        href="/hr-admin/hr-enquiries"
                        className="rbt-job-overlay-view-all"
                        onClick={notificationHandlers.enquiries}
                      >
                        View All Enquiries →
                      </Link>
                    </div>
                  </>
                )}
                <div className="rbt-notification-divider"></div>
              </div>
            )}

            {showJobs && (
              <NotificationSection
                title="Student Responses"
                notifications={notifications.selections.data}
                loading={notifications.selections.loading}
                renderItem={(n) => (
                  <li
                    key={n._id}
                    className="rbt-job-overlay-item rbt-selection-notification unread"
                    onClick={() => notificationHandlers.selections(n._id)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="rbt-job-overlay-content">
                      <div className="rbt-selection-notification-header">
                        <div className="rbt-job-overlay-title-text">
                          <strong>{n.title}</strong>
                          <span className="rbt-job-overlay-time">{formatDate(n.createdAt)}</span>
                        </div>
                      </div>
                      <div className="rbt-job-overlay-company">{n.message}</div>
                      <button
                        className={styles.viewDetailsBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          notificationHandlers.selections(n._id);
                        }}
                      >
                        <span>View Details</span>
                        <i className="bi bi-arrow-right"></i>
                      </button>
                    </div>
                  </li>
                )}
              />
            )}

            {activeTabCountMap[activeTab] === 0 && !activeTabLoadingMap[activeTab] && (
              <div className="p-3 text-center text-muted">No new notifications</div>
            )}
          </div>
        )}
      </li>
    </>
  );
};

export default HRNotifications;
